import React, { useState } from 'react';
import { Play, ArrowLeft, ExternalLink } from 'lucide-react';
import { motion } from 'motion/react';
import { useTranslate } from '../context/LanguageContext';
import { useData } from '../context/DataContext';
import { cn } from '../lib/utils';

/**
 * Extract YouTube video ID from various URL formats:
 * - https://www.youtube.com/watch?v=ID
 * - https://youtu.be/ID
 * - https://www.youtube.com/embed/ID
 */
function extractYouTubeId(url: string): string | null {
  if (!url) return null;
  const patterns = [
    /[?&]v=([a-zA-Z0-9_-]{11})/,
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /\/embed\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const pattern of patterns) {
    const m = url.match(pattern);
    if (m) return m[1];
  }
  return null;
}

function getThumbnail(video: { thumbnailUrl?: string; videoUrl?: string }): string {
  if (video.thumbnailUrl && !video.thumbnailUrl.includes('placeholder')) {
    return video.thumbnailUrl;
  }
  const ytId = extractYouTubeId(video.videoUrl || '');
  if (ytId) return `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`;
  return video.thumbnailUrl || '';
}

function getVideoHref(video: { videoUrl?: string; id?: string | number }): string {
  if (video.videoUrl) return video.videoUrl;
  return '#';
}

function getTranslatedTitle(video: any, language: string): string {
  if (language === 'ar') return video.title;
  return video.title_en || video.title;
}

export default function VideoLibrary() {
  const { language, t } = useTranslate();
  const { videos: dynamicVideos, isLoading } = useData();
  const [activeVideo, setActiveVideo] = useState<string | null>(null);

  // Use dynamic videos from API
  const videos = dynamicVideos || [];

  const featured = videos[0];
  const others = videos.slice(1, 4);

  const featuredYtId = featured ? extractYouTubeId(featured.videoUrl || '') : null;

  return (
    <section className="bg-primary py-24 text-white relative overflow-hidden animate-fade-in" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-12 border-b border-white/10 pb-6 relative z-10">
          <h2 className="text-2xl sm:text-3.5xl font-black relative text-white tracking-tight pb-2 group cursor-pointer">
            {t('المكتب المرئي (الفيديو)')}
            <span className={cn("absolute -bottom-[25px] w-32 h-1 bg-secondary shadow-[0_4px_12px_rgba(255,63,52,0.4)] transition-all duration-300 group-hover:w-full", language === 'ar' ? 'right-0' : 'left-0')} />
          </h2>
          <a href="/videos" className="text-xs font-bold text-zinc-400 hover:text-secondary flex items-center gap-1 transition-colors group">
            {t('مشاهدة الكل')} <ArrowLeft size={14} className={cn("transition-transform duration-300", language === 'ar' ? "group-hover:-translate-x-1" : "group-hover:translate-x-1 rotate-180")} />
          </a>
        </div>

        {/* Empty state */}
        {!isLoading && videos.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 border border-white/5 rounded-3xl bg-white/5">
            <span className="text-zinc-500 text-sm">
              {language === 'ar' ? 'لم يتم نشر أي مقاطع فيديو بعد.' : 'No videos have been published yet.'}
            </span>
          </div>
        )}

        {/* Loading skeleton */}
        {isLoading && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 animate-pulse">
            <div className="lg:col-span-8 aspect-video bg-white/10 rounded-3xl" />
            <div className="lg:col-span-4 flex flex-col gap-6">
              {[1,2,3].map(i => (
                <div key={i} className="flex gap-4">
                  <div className="w-32 h-24 bg-white/10 rounded-xl flex-shrink-0" />
                  <div className="flex-1 space-y-3 py-2">
                    <div className="h-3 bg-white/10 rounded w-4/5" />
                    <div className="h-3 bg-white/10 rounded w-3/5" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!isLoading && featured && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Featured Video */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="lg:col-span-8 group relative aspect-video overflow-hidden border-4 border-zinc-800 rounded-3xl shadow-2xl cursor-pointer"
              onClick={() => {
                if (featuredYtId) {
                  setActiveVideo(featuredYtId);
                } else if (featured.videoUrl) {
                  window.open(featured.videoUrl, '_blank');
                }
              }}
            >
              {/* Inline YouTube embed on click */}
              {activeVideo === featuredYtId && featuredYtId ? (
                <iframe
                  src={`https://www.youtube.com/embed/${featuredYtId}?autoplay=1`}
                  className="w-full h-full"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  title={getTranslatedTitle(featured, language)}
                />
              ) : (
                <>
                  <img
                    src={getThumbnail(featured)}
                    alt={getTranslatedTitle(featured, language)}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-2xl">
                      <Play size={40} className={cn("fill-current", language === 'ar' ? "mr-2" : "ml-2")} />
                    </div>
                  </div>
                  <div className={cn("absolute bottom-0 right-0 left-0 p-6 bg-gradient-to-t from-black to-transparent", language === 'ar' ? "text-right" : "text-left")}>
                    <h3 className="text-[14px] font-normal">{getTranslatedTitle(featured, language)}</h3>
                  </div>
                </>
              )}
            </motion.div>

            {/* Sidebar Videos */}
            <div className="lg:col-span-4 flex flex-col gap-6">
              {others.map((video, i) => {
                const ytId = extractYouTubeId(video.videoUrl || '');
                const thumb = getThumbnail(video);
                return (
                  <motion.div
                    key={video.id}
                    initial={{ opacity: 0, x: language === 'ar' ? 20 : -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex gap-4 group cursor-pointer"
                    onClick={() => {
                      if (ytId) {
                        window.open(`https://www.youtube.com/watch?v=${ytId}`, '_blank');
                      } else if (video.videoUrl) {
                        window.open(video.videoUrl, '_blank');
                      }
                    }}
                  >
                    <div className="w-32 h-24 flex-shrink-0 relative overflow-hidden bg-zinc-800 rounded-xl shadow-lg">
                      {thumb ? (
                        <img
                          src={thumb}
                          alt={getTranslatedTitle(video, language)}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 opacity-60 group-hover:opacity-100"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-full h-full bg-zinc-700" />
                      )}
                      <div className="absolute inset-0 flex items-center justify-center">
                        {ytId ? (
                          <Play size={20} className="fill-current text-white drop-shadow" />
                        ) : (
                          <ExternalLink size={16} className="text-white opacity-80" />
                        )}
                      </div>
                    </div>
                    <div className={cn("flex flex-col justify-center", language === 'ar' ? "text-right" : "text-left")}>
                      <h4 className="text-[12px] font-normal group-hover:text-secondary transition-colors line-clamp-2 leading-relaxed">
                        {getTranslatedTitle(video, language)}
                      </h4>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty state if no videos at all */}
        {!isLoading && !featured && (
          <div className="text-center py-16 text-white/40 text-sm font-bold tracking-wide">
            {t('لا توجد مقاطع فيديو بعد. أضفها من لوحة التحكم.')}
          </div>
        )}
      </div>
    </section>
  );
}
